// ============================================================
// faceColour - what colour a scene face is drawn, and how solid.
//
// Pure, and in the engine rather than beside the renderer so anything that
// draws a map - the scene's SVG, the asset library's thumbnails on a
// canvas - colours a face the same way. Returns plain rgb() strings: no
// CSS variable reaches a canvas.
// ============================================================

import type { SceneFace } from './scene';

/** What something with no colour of its own is made of. */
const BASE = { r: 148, g: 152, b: 160 };

/**
 * The material's colour, shaded by which way the face points.
 *
 * The hazard tint applies ONLY to terrain that has no colour of its own -
 * the parametric presets. Before this, the tint was mixed into everything
 * at 55%, which swamped the palette: smoke and wood came out the same
 * ochre because neither had ever reached the renderer.
 */
export function faceFill(face: SceneFace): string {
  const s = face.shade;
  const own = face.colour ? hexToRgb(face.colour) : null;
  const source = own ?? BASE;
  const base = { r: source.r * s, g: source.g * s, b: source.b * s };
  if (own || !face.tint) return `rgb(${base.r | 0},${base.g | 0},${base.b | 0})`;
  const t = hexToRgb(face.tint);
  const mix = 0.55;
  return `rgb(${(base.r * (1 - mix) + t.r * s * mix) | 0},${(base.g * (1 - mix) + t.g * s * mix) | 0},${
    (base.b * (1 - mix) + t.b * s * mix) | 0
  })`;
}

/**
 * How solid it LOOKS, from how solid it IS.
 *
 * Smoke you can shoot through should not be drawn as a brick. The
 * numbers are chosen so a translucent volume still reads as a volume -
 * you must be able to see it is there and see through it at once.
 */
export function faceOpacity(face: SceneFace): number | undefined {
  if (face.transparency === 1) return 0.55;
  if (face.transparency === 2) return 0.35;
  return undefined;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const n = parseInt(hex.replace('#', ''), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
